import React, { useEffect, useState, useRef, useCallback } from "react";
import { Outlet, useSearchParams, useNavigate } from "react-router-dom";
import Loader from "../utils/Loader";
import { setFlagUser, setOktaUser } from "../slices/userSlice";
import { useOktaAuth } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL } from "../constant/apiUrl";
import { getAPI } from "../API/ApiService";
import {
  setInternalGroups,
  setInternalRoutes,
  setUserNavigations,
} from "../slices/navSlice";
import { setConfig } from "../slices/configSlice";
import {
  extractInternalGroups,
  extractInternalRoutes,
} from "../utils/function";
import {
  handleDeactivatedUserLogout,
  isDeactivatedUserResponse,
  performOktaLogout,
} from "../utils/deactivatedUserSession";

export const PrivateRoute = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { oktaAuth, authState } = useOktaAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newAuthState, setNewAuthState] = useState(authState);
  const [nav, setNav] = useState([]);
  const [userAccessVerified, setUserAccessVerified] = useState(false);
  const { External } = useSelector((store) => store.config);
  const inactivityTimerRef = useRef(null);
  const effectiveAuthState = newAuthState !== null ? newAuthState : authState;
  const { flagUser } = useSelector((store) => store.user);
  const { refetchNav } = useSelector((store) => store.nav);
  const hasFetchedUserRef = useRef(false);
  const fetchedRef = useRef(false);
  const deactivatedHandledRef = useRef(false);

  const handleDeactivatedUser = useCallback(async () => {
    if (deactivatedHandledRef.current) {
      return;
    }

    deactivatedHandledRef.current = true;
    await handleDeactivatedUserLogout(External);
    dispatch(setFlagUser({}));
  }, [dispatch, External]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (isVideoPlaying()) {
      return;
    }

    inactivityTimerRef.current = setTimeout(async () => {
      localStorage.removeItem("auth_token");
      dispatch(setConfig(null));
      await performOktaLogout(External);
    }, 15 * 60 * 1000);
  }, [dispatch, External]);

  const isVideoPlaying = () => {
    const videos = document.querySelectorAll("video");
    return Array.from(videos).some((video) => !video.paused);
  };

  useEffect(() => {
    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    const videos = document.querySelectorAll("video");
    const VideoEvents = ["play", "pause", "ended"];
    videos.forEach((video) => {
      VideoEvents.forEach((event) => {
        video.addEventListener(event, handleUserActivity);
      });
    });

    const Events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
    ];
    Events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      videos.forEach((video) => {
        VideoEvents.forEach((event) => {
          video.removeEventListener(event, handleUserActivity);
        });
      });

      Events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      try {
        if (authState?.isAuthenticated) {
          const accessToken = authState.accessToken;
          const currentTime = Math.floor(Date.now() / 1000);

          if (accessToken.expiresAt - currentTime < 60 && !isRefreshing) {
            setIsRefreshing(true);
            const { refreshToken, idToken } = JSON.parse(
              localStorage.getItem("okta-token-storage")
            );

            const tokenUrls = `${refreshToken.tokenUrl}`;
            const clientId = `${idToken.clientId}`;

            const body = new URLSearchParams();
            body.append("grant_type", "refresh_token");
            body.append("client_id", clientId);
            body.append(
              "refresh_token",
              refreshToken.refreshToken
                ? refreshToken.refreshToken
                : refreshToken
            );
            body.append("scope", "openid offline_access email profile");

            const response = await fetch(tokenUrls, {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: body.toString(),
            });

            if (!response.ok) {
              throw new Error("Failed to refresh the token");
            }

            const data = await response.json();
            const { access_token, refresh_token, expires_in } = data;

            const newData = await oktaAuth.authStateManager.updateAuthState();

            newData.refreshToken.refreshToken = refresh_token;
            newData.refreshToken.expiresAt =
              Math.floor(Date.now() / 1000) + expires_in;

            localStorage.setItem("auth_token", access_token);
            localStorage.setItem("okta-token-storage", JSON.stringify(newData));
            localStorage.setItem("auth_state", JSON.stringify(newData));

            setNewAuthState(newData);
          }
        }
      } catch (error) {
        console.error("Error refreshing access token:", error);
        navigate("/login", { replace: true });
      } finally {
        setIsRefreshing(false);
      }
    };

    const interval = setInterval(() => {
      checkAndRefreshToken();
    }, 600000);

    return () => clearInterval(interval);
  }, [oktaAuth, authState, isRefreshing, navigate]);

  useEffect(() => {
    if (!effectiveAuthState) {
      return;
    }

    if (!effectiveAuthState?.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      const external = searchParams.get("external");
      if (external === null) {
        navigate("/login", { replace: true });
      } else {
        const isExternal = external === "true";
        if (External === isExternal) {
          oktaAuth.signInWithRedirect();
        }
        dispatch(setConfig(external === "true"));
      }
    } else if (authState?.isAuthenticated) {
      localStorage.setItem("auth_token", authState?.accessToken?.accessToken);
    }
  }, [oktaAuth, authState, navigate, dispatch, effectiveAuthState, External]);

  useEffect(() => {
    if (!effectiveAuthState?.isAuthenticated) {
      return;
    }

    const fetch = async () => {
      try {
        const response = await getAPI(`${BASE_URL}/navigations/find-all`);

        if (response?.status === 200) {
          dispatch(setUserNavigations(response?.data));
          const result = extractInternalRoutes(response?.data);
          const groups = extractInternalGroups(response?.data);
          dispatch(setInternalGroups(groups));
          dispatch(setInternalRoutes(result));
          setNav(response?.data);
        }
      } catch (error) {
        console.error("Navigation fetch error", error);
      }
    };

    if (!fetchedRef.current || refetchNav) {
      fetchedRef.current = true;
      fetch();
    }
  }, [dispatch, refetchNav, effectiveAuthState?.isAuthenticated]);

  useEffect(() => {
    if (!effectiveAuthState?.isAuthenticated) {
      return;
    }

    const isEmptyObject = (obj) =>
      obj && Object.keys(obj).length === 0 && obj.constructor === Object;

    if (hasFetchedUserRef.current || !isEmptyObject(flagUser)) {
      if (!isEmptyObject(flagUser)) {
        setUserAccessVerified(true);
      }
      return;
    }

    hasFetchedUserRef.current = true;

    const fetchRole = async () => {
      try {
        const response = await getAPI(`${BASE_URL}/admin/users/getUserDetails`);

        if (isDeactivatedUserResponse(response)) {
          await handleDeactivatedUser();
          return;
        }

        if (response?.status === 200) {
          const userDetails = await oktaAuth.getUser();

          dispatch(setOktaUser(userDetails));
          dispatch(setFlagUser(response?.data));
          setUserAccessVerified(true);
        }
      } catch (e) {
        console.error("Error fetching user role", e);
      }
    };

    fetchRole();
  }, [
    dispatch,
    flagUser,
    oktaAuth,
    effectiveAuthState?.isAuthenticated,
    handleDeactivatedUser,
  ]);

  if (
    !effectiveAuthState ||
    !effectiveAuthState?.isAuthenticated ||
    nav?.length === 0 ||
    !userAccessVerified
  ) {
    return <Loader />;
  }

  return <Outlet />;
};

export default PrivateRoute;
